import { ThunkAction } from 'redux-thunk';
import { AnyAction } from 'redux';
import { AppState } from '../../store';
import * as Actions from './actions';
import * as PropertyActions from '../property/actions';
import { v2BaseApiPath } from '../../../config/baseConfig';
import { Property, hasSingleApplicationIdentifier } from '../property/types';
import { ApplicantType, ALREADY_APPLIED_CODE, verifyInvitationErrorStatus } from './types';

export const isOccupantOnly = (type: ApplicantType): boolean => type === ApplicantType.Occupant;

export const isContractorOnly = (type: ApplicantType): boolean => type === ApplicantType.Contractor;

/** ステータス → 失敗理由。409 は code で申込済み / キャンセルを分ける */
const toErrorStatus = (httpStatus: number, code?: string): verifyInvitationErrorStatus => {
  switch (httpStatus) {
    case 400:
    case 404:
      return 'not_found';
    case 403:
      return 'locked';
    case 409:
      return code === ALREADY_APPLIED_CODE ? 'already_applied' : 'canceled';
    default:
      return 'internal';
  }
};

export const toHyphenatedPostCode = (postcode?: string): string => {
  const digits = (postcode || '').replace(/[^0-9]/g, '');
  return digits.length === 7 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : postcode || '';
};

const toProperty = (json: any, organizationId: string, peopleId: string): Property => ({
  organizationId,
  contractInfoId: json.contractInfoId || '',
  usageInfoId: json.usageInfoId || '',
  usageId: json.usageId || '',
  allSpaceId: json.allSpaceId,
  contractId: json.contractId,
  peopleId,
  contractStartDate: json.contractStartDate,
  occupyScheduledDate: json.moveInDate ? new Date(json.moveInDate) : undefined,
  viewnStartDate: json.viewnStartDate,
  hasElectricContract: json.useSumamoruPlan,
  postCode: toHyphenatedPostCode(json.postcode),
  address: json.address,
  prefecture: json.prefecture || '',
  city: json.city || '',
  afterCity: json.afterCity || '',
  buildingName: json.buildingName || '',
  propertyName: json.roomName || '',
  propertyCode: json.propertyCode || '',
  sumamoruCode: json.sumamoruCode,
  managementCompanyAddress: json.managementCompanyAddress,
  managementCompanyName: json.managementCompanyName,
  managementCompanyPhoneNumber: json.managementCompanyPhoneNumber,
  sumamoruInstallAppTypes: json.installAppTypes,
  sumamoruProjectType: json.projectType,
  sumamoruAdditionalServices: json.additionalServices,
  applicantType: json.applicantType,
  isContractorSameAsOccupant: json.isContractorSameAsOccupant
});

export const verifyInvitation = (
  organizationId: string,
  contractInfoId: string,
  usageInfoId: string,
  peopleId: string
): ThunkAction<Promise<void>, AppState, {}, AnyAction> => async dispatch => {
  try {
    dispatch(Actions.verifyInvitation());

    if (!organizationId || !hasSingleApplicationIdentifier(contractInfoId, usageInfoId) || !peopleId) {
      dispatch(Actions.failedToVerifyInvitation('invalid_request'));
      return;
    }

    const query = new URLSearchParams({
      organizationId,
      ...(contractInfoId ? { contractInfoId } : { usageInfoId }),
      peopleId
    });
    const endpoint = `${v2BaseApiPath}/application/invitation?${query}`;
    // 未ログインのブラウザから呼ぶ無認証API。GET なので Content-Type も付けない
    const headers = { Accept: 'application/json' };
    const method = 'GET';

    const res = await fetch(endpoint, {
      method,
      headers,
      mode: 'cors'
    });

    const json = await res.json().catch(() => undefined);

    if (res.status !== 200) {
      const code = json && json.code;
      console.log('failed to verify invitation.', res.status, code);
      dispatch(PropertyActions.failedSetProperty());
      dispatch(Actions.failedToVerifyInvitation(toErrorStatus(res.status, code)));
      return;
    }

    dispatch(PropertyActions.setProperty(toProperty(json, organizationId, peopleId)));
    dispatch(Actions.successToVerifyInvitation());
  } catch (error) {
    console.error(error);
    dispatch(PropertyActions.failedSetProperty());
    dispatch(Actions.failedToVerifyInvitation('internal'));
  }
};
